import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { uploadAPI } from '../services/api';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import notificationSound from '../utils/notificationSound';
import { usePushNotifications } from '../hooks/usePushNotifications';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  User,
  Mail,
  Settings,
  LogOut,
  ChevronRight,
  Edit,
  Bell,
  Lock,
  Save,
  X,
  Loader2,
  Phone,
  Camera,
  RefreshCw,
  MapPin,
  Calendar,
  Heart,
  BookOpen,
  Volume2,
  BellRing,
  CheckCircle,
  HelpCircle
} from 'lucide-react';
import BottomNav from '../components/BottomNav';

const ProfilePage = () => {
  const { user, logout, updateProfile, isAdmin } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [activeSection, setActiveSection] = useState('profile');
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [avatarPreview, setAvatarPreview] = useState(null);
  
  // Push notifications hook
  const {
    isSupported: pushSupported,
    permission: pushPermission,
    isSubscribed: pushSubscribed,
    isLoading: pushLoading,
    requestPermission,
    subscribe: subscribeToPush,
    unsubscribe: unsubscribeFromPush,
    testNotification
  } = usePushNotifications();

  const [profileForm, setProfileForm] = useState({
    firstName: user?.firstName || '',
    lastName: user?.lastName || '',
    email: user?.email || '',
    phone: user?.phone || '',
    mailingAddress: user?.mailingAddress || '',
    birthday: user?.birthday || '',
    weddingAnniversary: user?.weddingAnniversary || '',
    spouseName: user?.spouseName || '',
    favoriteScripture: user?.favoriteScripture || '',
    bio: user?.bio || '',
    avatar: user?.avatar || ''
  });

  const [settings, setSettings] = useState({
    notifications: true,
    emailAlerts: true,
    privateProfile: user?.privateProfile || false,
    soundEnabled: notificationSound.getEnabled()
  });
  
  // Handle push notification toggle
  const handlePushToggle = async (enabled) => {
    if (enabled) {
      // Enable push notifications
      const granted = await requestPermission();
      if (granted) {
        const subscribed = await subscribeToPush();
        if (subscribed) {
          alert('Push notifications enabled! You will now receive notifications on this device.');
        }
      } else {
        alert('Permission denied. Please enable notifications in your browser settings.');
      }
    } else {
      // Disable push notifications
      await unsubscribeFromPush();
      alert('Push notifications disabled for this device.');
    }
  };
  
  const handleTestNotification = async () => {
    const success = await testNotification();
    if (success) {
      alert('Test notification sent! Check your notifications.');
    } else {
      alert('Failed to send test notification. Make sure notifications are enabled.');
    }
  };

  useEffect(() => {
    if (user) {
      setProfileForm({
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        email: user.email || '',
        phone: formatPhoneNumber(user.phone) || '',
        mailingAddress: user.mailingAddress || '',
        birthday: user.birthday || '',
        weddingAnniversary: user.weddingAnniversary || '',
        spouseName: user.spouseName || '',
        favoriteScripture: user.favoriteScripture || '',
        bio: user.bio || '',
        avatar: user.avatar || ''
      });
      setSettings(prev => ({
        ...prev,
        privateProfile: user.privateProfile || false
      }));
    }
  }, [user]);

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  // Format phone number as (XXX) XXX-XXXX
  const formatPhoneNumber = (value) => {
    if (!value) return '';
    // Remove all non-digits
    const phoneNumber = value.replace(/\D/g, '');
    const phoneLength = phoneNumber.length;
    
    if (phoneLength < 4) return phoneNumber;
    if (phoneLength < 7) {
      return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3)}`;
    }
    return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6, 10)}`;
  };

  // Handle phone input with formatting
  const handlePhoneChange = (e) => {
    const formatted = formatPhoneNumber(e.target.value);
    setProfileForm({ ...profileForm, phone: formatted });
  };

  // Format date for display (handles timezone issues by parsing as local date)
  const formatDateLocal = (dateStr, options = { month: 'long', day: 'numeric', year: 'numeric' }) => {
    if (!dateStr) return '';
    // If date is in YYYY-MM-DD format, parse it as local date without timezone conversion
    if (dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
      const [year, month, day] = dateStr.split('-');
      return new Date(year, month - 1, day).toLocaleDateString('en-US', options);
    }
    // Handle ISO date strings by extracting just the date part
    if (dateStr.includes('T')) {
      const datePart = dateStr.split('T')[0];
      const [year, month, day] = datePart.split('-');
      return new Date(year, month - 1, day).toLocaleDateString('en-US', options);
    }
    return dateStr;
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      alert('Please select an image file');
      return;
    }

    // Check file size - warn if over 10MB
    const MAX_SIZE_MB = 10;
    const fileSizeMB = file.size / (1024 * 1024);
    if (fileSizeMB > MAX_SIZE_MB) {
      alert(`Photo is too large (${fileSizeMB.toFixed(1)}MB). Please select an image under ${MAX_SIZE_MB}MB or use a smaller/compressed version.`);
      return;
    }

    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setAvatarPreview(reader.result);
    };
    reader.readAsDataURL(file);

    setUploading(true);
    try {
      const result = await uploadAPI.uploadFile(file);
      // Store only the relative URL path in the database (e.g., /api/uploads/filename.jpg)
      // This ensures portability across different environments (preview, production)
      const relativeUrl = result.url;
      setProfileForm({ ...profileForm, avatar: relativeUrl });
      // Auto-save avatar with relative URL
      await updateProfile({ avatar: relativeUrl });
      alert('Photo uploaded successfully!');
    } catch (err) {
      console.error('Upload error:', err);
      alert('Failed to upload photo. Please try a smaller image or check your connection.');
      setAvatarPreview(null);
    } finally {
      setUploading(false);
    }
  };

  const handleSaveProfile = async () => {
    setSaving(true);
    try {
      await updateProfile(profileForm);
      setIsEditing(false);
    } catch (error) {
      console.error('Error saving profile:', error);
    } finally {
      setSaving(false);
    }
  };

  const handlePrivacyChange = async (checked) => {
    setSettings({ ...settings, privateProfile: checked });
    try {
      await updateProfile({ privateProfile: checked });
    } catch (error) {
      console.error('Error updating privacy setting:', error);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const getBackendUrl = () => {
    return process.env.REACT_APP_BACKEND_URL || '';
  };

  const getAvatarUrl = () => {
    if (avatarPreview) return avatarPreview;
    if (profileForm.avatar) {
      // Handle Cloudinary URLs (new cloud storage format)
      if (profileForm.avatar.includes('cloudinary.com') || profileForm.avatar.includes('res.cloudinary.com')) {
        return profileForm.avatar;
      }
      // Handle relative URLs (legacy local format)
      if (profileForm.avatar.startsWith('/api/uploads/')) {
        return `${getBackendUrl()}${profileForm.avatar}`;
      }
      // Handle legacy absolute URLs from different environments
      // Extract just the path and use current backend URL
      if (profileForm.avatar.includes('/api/uploads/')) {
        const uploadPath = profileForm.avatar.substring(profileForm.avatar.indexOf('/api/uploads/'));
        return `${getBackendUrl()}${uploadPath}`;
      }
      // Fallback for any other format (including full URLs)
      return profileForm.avatar.startsWith('http') ? profileForm.avatar : `${getBackendUrl()}${profileForm.avatar}`;
    }
    return null;
  };

  const menuItems = [
    { id: 'profile', icon: User, label: 'My Profile' },
    { id: 'settings', icon: Settings, label: 'Settings' },
    { id: 'help', icon: HelpCircle, label: 'App Help', action: () => navigate('/help') },
  ];

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      {/* Header with gradient */}
      <div className="bg-gradient-to-br from-navy-900 to-navy-800 text-white px-5 pt-8 pb-8 rounded-b-3xl shadow-xl">
        <div className="flex items-center gap-4">
          {/* Photo Upload */}
          <input
            type="file"
            ref={fileInputRef}
            onChange={handlePhotoUpload}
            accept="image/*"
            className="hidden"
          />
          <div 
            className="relative cursor-pointer group"
            onClick={() => fileInputRef.current?.click()}
          >
            {getAvatarUrl() ? (
              <img 
                src={getAvatarUrl()}
                alt="Profile"
                className="w-18 h-18 rounded-full object-cover border-3 border-tan-500 shadow-lg"
              />
            ) : (
              <div className="w-18 h-18 rounded-full bg-gradient-to-br from-tan-400 to-tan-600 flex items-center justify-center text-2xl font-bold text-white shadow-lg">
                {user?.firstName?.charAt(0)}{user?.lastName?.charAt(0)}
              </div>
            )}
            <div className="absolute bottom-0 right-0 w-7 h-7 bg-white rounded-full flex items-center justify-center border-2 border-gray-100 shadow-md group-hover:bg-gray-50 transition-all duration-200">
              {uploading ? (
                <Loader2 className="w-3.5 h-3.5 text-navy-900 animate-spin" />
              ) : (
                <Camera className="w-3.5 h-3.5 text-navy-900" />
              )}
            </div>
          </div>
          <div>
            <h1 className="text-xl font-bold">{user?.firstName} {user?.lastName}</h1>
            <p className="text-tan-400 text-sm font-medium">{user?.email || user?.phone}</p>
          </div>
        </div>
      </div>

      <div className="px-4 -mt-4 space-y-4">
        {/* Menu Card */}
        <Card className="border-0 shadow-lg rounded-2xl overflow-hidden">
          <CardContent className="p-0">
            {menuItems.map((item) => (
              <button
                key={item.id}
                onClick={() => item.action ? item.action() : setActiveSection(item.id)}
                className={`w-full flex items-center justify-between p-4 border-b border-gray-100 last:border-0 transition-colors ${
                  activeSection === item.id ? 'bg-navy-50' : 'hover:bg-gray-50'
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                    activeSection === item.id ? 'bg-navy-900' : 'bg-gray-100'
                  }`}>
                    <item.icon className={`w-5 h-5 ${
                      activeSection === item.id ? 'text-white' : 'text-gray-600'
                    }`} />
                  </div>
                  <span className={`font-medium ${
                    activeSection === item.id ? 'text-navy-900' : 'text-gray-700'
                  }`}>{item.label}</span>
                </div>
                <div className="flex items-center gap-2">
                  {item.badge > 0 && (
                    <span className="w-6 h-6 bg-tan-500 text-white text-xs rounded-full flex items-center justify-center font-medium">
                      {item.badge}
                    </span>
                  )}
                  <ChevronRight className="w-5 h-5 text-gray-400" />
                </div>
              </button>
            ))}
          </CardContent>
        </Card>

        {/* Content Section */}
        {activeSection === 'profile' && (
          <Card className="border-0 shadow-lg rounded-2xl overflow-hidden">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-bold text-navy-900">Your Profile</h3>
                {!isEditing ? (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setIsEditing(true)}
                    className="rounded-lg"
                  >
                    <Edit className="w-4 h-4 mr-1" />
                    Edit
                  </Button>
                ) : (
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setIsEditing(false)}
                      className="rounded-lg"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                    <Button
                      size="sm"
                      className="bg-tan-500 hover:bg-tan-600 text-white rounded-lg"
                      onClick={handleSaveProfile}
                      disabled={saving}
                    >
                      {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : (
                        <><Save className="w-4 h-4 mr-1" />Save</>
                      )}
                    </Button>
                  </div>
                )}
              </div>

              {isEditing ? (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-navy-900">First Name</Label>
                      <Input
                        value={profileForm.firstName}
                        onChange={(e) => setProfileForm({ ...profileForm, firstName: e.target.value })}
                        className="h-11 bg-gray-50 border-gray-200 rounded-lg mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-navy-900">Last Name</Label>
                      <Input
                        value={profileForm.lastName}
                        onChange={(e) => setProfileForm({ ...profileForm, lastName: e.target.value })}
                        className="h-11 bg-gray-50 border-gray-200 rounded-lg mt-1"
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="text-navy-900">Email</Label>
                    <Input
                      type="email"
                      value={profileForm.email}
                      onChange={(e) => setProfileForm({ ...profileForm, email: e.target.value })}
                      className="h-11 bg-gray-50 border-gray-200 rounded-lg mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-navy-900">Phone</Label>
                    <Input
                      type="tel"
                      value={profileForm.phone}
                      onChange={handlePhoneChange}
                      className="h-11 bg-gray-50 border-gray-200 rounded-lg mt-1"
                      placeholder="(555) 555-5555"
                    />
                  </div>
                  <div>
                    <Label className="text-navy-900">Mailing Address</Label>
                    <Input
                      value={profileForm.mailingAddress}
                      onChange={(e) => setProfileForm({ ...profileForm, mailingAddress: e.target.value })}
                      className="h-11 bg-gray-50 border-gray-200 rounded-lg mt-1"
                      placeholder="123 Main St, City, State ZIP"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-navy-900">Birthday</Label>
                      <Input
                        type="date"
                        value={profileForm.birthday}
                        onChange={(e) => setProfileForm({ ...profileForm, birthday: e.target.value })}
                        className="h-11 bg-gray-50 border-gray-200 rounded-lg mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-navy-900">Anniversary</Label>
                      <Input
                        type="date"
                        value={profileForm.weddingAnniversary}
                        onChange={(e) => setProfileForm({ ...profileForm, weddingAnniversary: e.target.value })}
                        className="h-11 bg-gray-50 border-gray-200 rounded-lg mt-1"
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="text-navy-900">Spouse Name</Label>
                    <Input
                      value={profileForm.spouseName}
                      onChange={(e) => setProfileForm({ ...profileForm, spouseName: e.target.value })}
                      className="h-11 bg-gray-50 border-gray-200 rounded-lg mt-1"
                      placeholder="Enter spouse name (optional)"
                    />
                  </div>
                  <div>
                    <Label className="text-navy-900">Favorite Scripture</Label>
                    <Input
                      value={profileForm.favoriteScripture}
                      onChange={(e) => setProfileForm({ ...profileForm, favoriteScripture: e.target.value })}
                      className="h-11 bg-gray-50 border-gray-200 rounded-lg mt-1"
                      placeholder="e.g., John 3:16"
                    />
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                    <Mail className="w-5 h-5 text-tan-600" />
                    <div>
                      <p className="text-xs text-gray-500">Email</p>
                      <span className="text-sm text-navy-900">{user?.email || 'Not set'}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                    <Phone className="w-5 h-5 text-tan-600" />
                    <div>
                      <p className="text-xs text-gray-500">Phone</p>
                      <span className="text-sm text-navy-900">{user?.phone ? formatPhoneNumber(user.phone) : 'Not set'}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                    <MapPin className="w-5 h-5 text-tan-600" />
                    <div>
                      <p className="text-xs text-gray-500">Address</p>
                      <span className="text-sm text-navy-900">{user?.mailingAddress || 'Not set'}</span>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                      <Calendar className="w-5 h-5 text-tan-600" />
                      <div>
                        <p className="text-xs text-gray-500">Birthday</p>
                        <span className="text-sm text-navy-900">{user?.birthday ? formatDateLocal(user.birthday, { month: 'long', day: 'numeric', year: 'numeric' }) : 'Not set'}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                      <Heart className="w-5 h-5 text-tan-600" />
                      <div>
                        <p className="text-xs text-gray-500">Anniversary</p>
                        <span className="text-sm text-navy-900">{user?.weddingAnniversary ? formatDateLocal(user.weddingAnniversary, { month: 'long', day: 'numeric', year: 'numeric' }) : 'Not set'}</span>
                      </div>
                    </div>
                  </div>
                  {user?.spouseName && (
                    <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                      <User className="w-5 h-5 text-tan-600" />
                      <div>
                        <p className="text-xs text-gray-500">Spouse</p>
                        <span className="text-sm text-navy-900">{user.spouseName}</span>
                      </div>
                    </div>
                  )}
                  <div className="flex items-start gap-3 p-3 bg-tan-50 rounded-xl">
                    <BookOpen className="w-5 h-5 text-tan-600 mt-0.5" />
                    <div>
                      <p className="text-xs text-gray-500">Favorite Scripture</p>
                      <span className="text-sm text-navy-900 italic">{user?.favoriteScripture ? `"${user.favoriteScripture}"` : 'Not set'}</span>
                    </div>
                  </div>
                  <div className="text-xs text-gray-400 pt-2">
                    Member since {user?.memberSince || 'Recently joined'}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {activeSection === 'settings' && (
          <Card className="border-0 shadow-lg rounded-2xl overflow-hidden">
            <CardContent className="p-5">
              <div className="space-y-6">
                <div>
                  <h3 className="font-bold text-navy-900 mb-4">Notifications</h3>
                  <div className="space-y-4">
                    {/* Push Notifications */}
                    <div className="p-4 bg-gray-50 rounded-xl">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-3">
                          <BellRing className="w-5 h-5 text-tan-600" />
                          <Label className="font-semibold">Push Notifications</Label>
                        </div>
                        {pushLoading ? (
                          <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
                        ) : (
                          <Switch
                            checked={pushSubscribed}
                            onCheckedChange={handlePushToggle}
                            disabled={!pushSupported || pushLoading}
                          />
                        )}
                      </div>
                      {pushSupported ? (
                        <>
                          <p className="text-sm text-gray-600 mb-3">
                            Get notified when you receive messages, prayer requests, announcements, and birthday wishes!
                          </p>
                          {pushSubscribed ? (
                            <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                              <div className="flex items-center gap-2 mb-2">
                                <CheckCircle className="w-5 h-5 text-green-500" />
                                <span className="text-sm font-medium text-green-700">Notifications are ON</span>
                              </div>
                              <Button
                                variant="outline"
                                size="sm"
                                className="w-full mt-2 border-green-300 text-green-700 hover:bg-green-100"
                                onClick={handleTestNotification}
                              >
                                <Bell className="w-4 h-4 mr-2" />
                                Send Test Notification
                              </Button>
                            </div>
                          ) : (
                            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                              <p className="text-sm text-amber-800 font-medium mb-2">
                                Notifications are OFF
                              </p>
                              <p className="text-xs text-amber-700 mb-3">
                                Enable notifications so you never miss a message from your church family!
                              </p>
                              <Button
                                size="sm"
                                className="w-full bg-tan-500 hover:bg-tan-600 text-white"
                                onClick={() => handlePushToggle(true)}
                                disabled={pushLoading}
                              >
                                {pushLoading ? (
                                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                ) : (
                                  <Bell className="w-4 h-4 mr-2" />
                                )}
                                Enable Notifications
                              </Button>
                            </div>
                          )}
                          {pushPermission === 'denied' && (
                            <div className="bg-red-50 border border-red-200 rounded-lg p-3 mt-3">
                              <p className="text-sm text-red-700 font-medium mb-1">
                                Notifications are blocked
                              </p>
                              <p className="text-xs text-red-600">
                                To enable: Open your browser settings → Site Settings → Notifications → Allow for this site
                              </p>
                            </div>
                          )}
                        </>
                      ) : (
                        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                          <p className="text-sm text-amber-700">
                            Push notifications are not supported in this browser. Try using Chrome or Safari.
                          </p>
                        </div>
                      )}
                    </div>
                    
                    {/* Sound Notifications */}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <Volume2 className="w-5 h-5 text-tan-600" />
                        <Label>Notification Sounds</Label>
                      </div>
                      <Switch
                        checked={settings.soundEnabled}
                        onCheckedChange={(checked) => {
                          setSettings({ ...settings, soundEnabled: checked });
                          notificationSound.setEnabled(checked);
                          if (checked) {
                            // Play a test sound when enabled
                            notificationSound.playNotificationSound();
                          }
                        }}
                      />
                    </div>
                    <p className="text-xs text-gray-500 ml-8">
                      Play a sound when new messages arrive while using the app
                    </p>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <Mail className="w-5 h-5 text-tan-600" />
                        <Label>Email Alerts</Label>
                      </div>
                      <Switch
                        checked={settings.emailAlerts}
                        onCheckedChange={(checked) => setSettings({ ...settings, emailAlerts: checked })}
                      />
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="font-bold text-navy-900 mb-4">Privacy</h3>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Lock className="w-5 h-5 text-tan-600" />
                      <Label>Private Profile</Label>
                    </div>
                    <Switch
                      checked={settings.privateProfile}
                      onCheckedChange={handlePrivacyChange}
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-2 ml-8">
                    When enabled, only your name and role are visible to other members
                  </p>
                </div>

                <div>
                  <h3 className="font-bold text-navy-900 mb-4">App Updates</h3>
                  <p className="text-sm text-gray-600 mb-3">
                    If the app isn&apos;t showing the latest updates, tap below to refresh.
                  </p>
                  <Button
                    variant="outline"
                    className="w-full h-11 rounded-xl border-navy-200 text-navy-900 hover:bg-navy-50"
                    onClick={async () => {
                      try {
                        // Clear all caches
                        if ('caches' in window) {
                          const cacheNames = await caches.keys();
                          await Promise.all(cacheNames.map(name => caches.delete(name)));
                        }
                        // Unregister service worker
                        if ('serviceWorker' in navigator) {
                          const registrations = await navigator.serviceWorker.getRegistrations();
                          await Promise.all(registrations.map(r => r.unregister()));
                        }
                        // Clear localStorage cache markers
                        Object.keys(localStorage).forEach(key => {
                          if (key.startsWith('cache_')) localStorage.removeItem(key);
                        });
                        // Force reload
                        window.location.href = window.location.origin + '?refresh=' + Date.now();
                      } catch (error) {
                        console.error('Error clearing cache:', error);
                        window.location.reload(true);
                      }
                    }}
                  >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Clear Cache & Refresh App
                  </Button>
                </div>

                <div className="pt-4 border-t border-gray-100">
                  <Button
                    variant="destructive"
                    className="w-full h-11 rounded-xl"
                    onClick={handleLogout}
                  >
                    <LogOut className="w-4 h-4 mr-2" />
                    Sign Out
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      <BottomNav />
    </div>
  );
};

export default ProfilePage;
